/* *********************************************************************************************************************
 * In-house version of react-image-magnify. Since we have a clear picture of all the scenarios in which a zoomable
 * image is needed, we can create a more efficient and less complex version of the component. This allows us
 * to reduce our bundle size and have a more performant component, while at the same time having the
 * flexibility to add/ remove features as we see fit.
 *
 * TODO: Make this component compatible in mobile. In such cases, the zoom window should not exist but rahter we
 *  need to provide +/ - toggles that increase and decrease the size of the image thumbnail.
 *
 *  @format
 * *********************************************************************************************************************
 * */

import React, {useState, useRef, useEffect} from 'react';
import {makePictureTree} from 'filestack-adaptive';
import {AdaptiveImage} from '../adaptive_image';
import {createPortal} from 'react-dom';
import {If} from 'frontier-utilities';

export const MagnifyImageBeta = props => {
    const {src, alt, minZoom = 1, maxZoom = 8, className = '', imageClassName} = props;

    let {width = 0, height = 0} = props;

    const [zoomWindowSize, setZoomWindowSize] = useState({width: 200, height: 200});
    const [zoomPosition, setZoomPosition] = useState({top: 0, left: 0});
    const [imagePosition, setImagePosition] = useState({x: 0, y: 0});
    const [position, setPosition] = useState({x: 0, y: 0});
    // permits the zoom window to remain visible after the mouse leaves the image
    const [isShowZoomFixed, setIsShowZoomFixed] = useState(false);
    const [showZoom, setShowZoom] = useState(false);
    const containerRef = useRef(null);
    const [zoomScale, setZoomScale] = useState(1);
    const imageRef = useRef(null);

    /* *************************************************************************
     * Some images can be REALLY big, We want the size of the image or
     * 10k resolution max
     * *************************************************************************/
    width = Math.min(10000, width);
    height = Math.min(10000, height);

    /* *************************************************************************
     * TODO: Automatic-rendering option: If the image is too close to the edges
     *  of the screen, we don't want to show the zoom outside the view port.
     *  For this we need to calculate what is the best position to show the
     *  zoom window.
     * *************************************************************************/
    const calculateZoomPosition = () => {
        if (!imageRef.current || !containerRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();

        // What is the actual painted size of the image
        let renderedImageWidth = imageRef.current.offsetWidth;
        let renderedImageHeight = imageRef.current.offsetHeight;

        const position = {top: 0, left: 0};
        position.top = rect.y;
        position.left = rect.x + renderedImageWidth + 20;

        // The width of the preview should calculate how much space we have on the left and thus render the image
        // preview size accordingly
        const freeSpace = window.innerWidth - position.left;

        // Calculate dimensions while preserving aspect ratio
        const aspectRatio = width / height;
        let imageSize = {width: 0, height: 0};

        // Start with max dimensions
        const maxDimension = 500;

        if (aspectRatio >= 1) {
            // Wider than tall
            imageSize.width = Math.min(freeSpace * 0.95 - 20, maxDimension);
            imageSize.height = imageSize.width / aspectRatio;
        } else {
            // Taller than wide
            imageSize.height = Math.min(freeSpace * 0.95 - 20, maxDimension);
            imageSize.width = imageSize.height * aspectRatio;

            // If width exceeds max, recalculate
            if (imageSize.width > maxDimension) {
                imageSize.width = maxDimension;
                imageSize.height = maxDimension / aspectRatio;
            }
        }

        // If the free space is less then the size of the image then render it at the bottom
        const shouldRenderBottom = freeSpace < renderedImageWidth;

        // check if image size will fit in the window W viewport, otherwise render it on the H viewport
        if (shouldRenderBottom) {
            position.top = rect.y + renderedImageHeight + 20;
            position.left = rect.x;

            // Still maintain max dimensions
            if (renderedImageWidth > maxDimension || renderedImageHeight > maxDimension) {
                if (aspectRatio >= 1) {
                    imageSize.width = Math.min(renderedImageWidth, maxDimension);
                    imageSize.height = imageSize.width / aspectRatio;
                } else {
                    imageSize.height = Math.min(renderedImageHeight, maxDimension);
                    imageSize.width = imageSize.height * aspectRatio;
                }
            } else {
                imageSize.width = renderedImageWidth;
                imageSize.height = renderedImageHeight;
            }
        }

        setZoomWindowSize(imageSize);
        setZoomPosition(position);
    };

    /* *************************************************************************
     * Based on the parameters set, create the preview box that will show the
     * magnified image.
     *
     * TODO: Create a component to catch all the hotkeys, this might lead to
     *  conflicts with other components.
     * *************************************************************************/
    const getPreviewBox = () => {
        if (!showZoom) return null;

        const previewWidth = 100 / zoomScale;
        const previewHeight = 100 / zoomScale;

        return {
            left: `${position.x - previewWidth / 2}%`,
            top: `${position.y - previewHeight / 2}%`,
            width: `${previewWidth}%`,
            height: `${previewHeight}%`
        };
    };

    useEffect(() => {
        calculateZoomPosition();
        // window.addEventListener('resize', calculateZoomPosition);
        // return () => window.removeEventListener('resize', calculateZoomPosition);
    }, []);

    /* *************************************************************************
     * The users need to see where the cursor is on the image being zoomed in so
     * they can navigate it. This will calculate that.
     * *************************************************************************/
    const handleMouseMove = e => {
        if (!imageRef.current) return;

        const rect = imageRef.current.getBoundingClientRect();

        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        setPosition({x, y});
    };

    /* *************************************************************************
     * TODO: Might be able to get rid of. The idea of this function is was to
     *  obtain a more accurate position of the cursor inside the image.
     *  After running into multiple edge cases, I am allowing the
     *  cursor to be outside the image to account for the
     *  unreachable areas for now.
     * *************************************************************************/

    function handleSetImagePosition(e, rect) {
        const cursorPosition = {left: e.clientX, top: e.clientY};
        const imagePosition = {left: rect.left, top: rect.top};

        // how far from the top is the cursor in the image
        const cursorPositionIntoTheImageY =
            ((cursorPosition.top - imagePosition.top) / rect.height) * 100;
        const cursorPositionIntoTheImageX =
            ((cursorPosition.left - imagePosition.left) / rect.width) * 100;
        const sizeOfTheMagnifier = 100 / zoomScale / 2;

        const imagePositionTop = cursorPositionIntoTheImageY - sizeOfTheMagnifier;
        const imagePositionLeft = cursorPositionIntoTheImageX - sizeOfTheMagnifier;
        setImagePosition({x: imagePositionLeft, y: imagePositionTop});
    }

    /* *************************************************************************
     * Since the wheel event is responsible for zooming in and out, we want to
     * prevent its default behavior (scrolling), since this will make the
     * page move up or down as the user tries to adjust the zoom
     * intensity.
     * *************************************************************************/
    const handleWheel = e => {
        if (showZoom) {
            e.preventDefault();
            const delta = e.deltaY * -0.01;
            const newZoom = Math.min(Math.max(zoomScale + delta, minZoom), maxZoom);
            setZoomScale(newZoom);
        }
    };

    // Setup wheel event listeners when component mounts
    useEffect(() => {
        const imageElement = imageRef.current;
        if (imageElement) {
            imageElement.addEventListener('wheel', handleWheel, {passive: false});
        }

        return () => {
            if (imageElement) {
                imageElement.removeEventListener('wheel', handleWheel);
            }
        };
    }, [showZoom, zoomScale, minZoom, maxZoom]);

    const handleMouseEnter = () => {
        calculateZoomPosition();
        setShowZoom(true);
    };

    const handleMouseLeave = () => {
        setShowZoom(false);
        setZoomScale(1); // Reset zoom level when mouse leaves
    };

    /* *************************************************************************
     * At times, users might not want the zoom image to go away after the cursor
     * leaves the image because they might be comparing the image to the specs
     * of the product or against costumer comments. This allows the user
     * to keep the image in the screen and move it around freely.
     * *************************************************************************/
    const handleTogglePreviewFixation = () => {
        setIsShowZoomFixed(!isShowZoomFixed);
    };

    const backgroundStyle = {
        backgroundImage:
            'linear-gradient(45deg, #9a9a9a 25%, transparent 25%), linear-gradient(-45deg, #9a9a9a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #9a9a9a 75%), linear-gradient(-45deg, transparent 75%, #9a9a9a 75%)',
        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
        backgroundSize: '20px 20px',
        backgroundColor: 'white',
        borderBox: 'box-sizing',
        padding: '20px',
        zIndex: 199
    };

    const tree = makePictureTree(
        {apiKey: process.env.FILESTACK_KEY, srcHandle: src},
        {
            sizes: {
                fallback: 1000 + 'px'
            },
            resolutions: [1000],
            cname: process.env.FILESTACK_CDN_URL?.replace('https://cdn.', '')
        }
    );

    const isFilePressed = width > 0 || height > 0;
    const previewBox = getPreviewBox();

    if (!src) return <>File Missing</>;

    return (
        <article className={`tw-relative ${className}`} ref={containerRef}>
            <div
                className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-cursor-zoom-in"
                style={backgroundStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}>
                <div
                    className="tw-overflow-hidden tw-w-full tw-h-full tw-relative"
                    //onClick={handleTogglePreviewFixation} // TODO: Work on this, scroll resets on fixation
                    ref={imageRef}>
                    <AdaptiveImage
                        className={`tw-w-full tw-h-auto tw-object-contain tw-block`}
                        imageClassName={`${imageClassName}`}
                        src={src}
                        alt={alt}
                    />
                    <If condition={isFilePressed && showZoom && previewBox}>
                        <div
                            className="tw-absolute tw-border-2 tw-border-blue-500 tw-pointer-events-none"
                            style={{
                                ...previewBox,
                                backgroundColor: 'rgba(59, 130, 246, 0.2)'
                            }}
                        />
                    </If>
                    <If condition={showZoom && !isFilePressed}>
                        <div className="tw-absolute tw-top-2 tw-left-2 tw-bg-gray-900/50 tw-text-white tw-text-sm  tw-p-2 tw-rounded-l">
                            File not pressed
                        </div>
                    </If>
                </div>

                <If condition={isFilePressed && (showZoom || isShowZoomFixed)}>
                    <ZoomPreview
                        // imagePosition={imagePosition}
                        zoomWindowSize={zoomWindowSize}
                        zoomPosition={zoomPosition}
                        zoomScale={zoomScale}
                        position={position}
                        src={src}
                    />
                </If>
            </div>
        </article>
    );
};

function ZoomPreview({src, position, zoomScale, zoomWindowSize, zoomPosition, imagePosition}) {
    const [dragging, setDragging] = useState(false);
    const [offset, setOffset] = useState({x: 0, y: 0});
    const previewRef = useRef(null);

    const handleMouseDown = e => {
        setDragging(true);
        setOffset({
            x: e.clientX - previewRef.current.getBoundingClientRect().left,
            y: e.clientY - previewRef.current.getBoundingClientRect().top
        });
    };

    const handleMouseMove = e => {
        if (dragging) {
            const newLeft = e.clientX - offset.x;
            const newTop = e.clientY - offset.y;
            previewRef.current.style.left = `${newLeft}px`;
            previewRef.current.style.top = `${newTop}px`;
        }
    };

    const handleMouseUp = () => {
        setDragging(false);
    };

    React.useEffect(() => {
        if (dragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        } else {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [dragging]);

    return createPortal(
        <div
            className="tw-fixed tw-bg-white tw-shadow-lg tw-rounded-lg tw-overflow-hidden"
            ref={previewRef}
            style={{
                cursor: dragging ? 'grabbing' : 'grab',
                height: `${zoomWindowSize.height}px`,
                width: `${zoomWindowSize.width}px`,
                maxHeight: '500px',
                maxWidth: '500px',
                left: zoomPosition.left,
                top: zoomPosition.top,
                zIndex: 1060
            }}
            onMouseDown={handleMouseDown}>
            <div
                className="tw-w-full tw-h-full"
                style={{
                    backgroundImage: `url(${src})`,
                    backgroundPosition: `${position.x}% ${position.y}%`,
                    backgroundSize: `${zoomScale * 100}%`,
                    backgroundRepeat: 'no-repeat'
                }}
            />
            <div className="tw-absolute tw-bottom-2 tw-right-2 tw-bg-black tw-bg-opacity-50 tw-text-white tw-px-2 tw-py-1 tw-rounded tw-text-sm">
                {Math.round(zoomScale * 100)}%
            </div>
        </div>,
        document.body
    );
}

export default ZoomPreview;
